import React, { useState, useEffect, useRef } from 'react';
import {
    Send,
    X,
    Image as ImageIcon,
    FileText,
    Download,
    Check,
    CheckCheck,
    Circle
} from 'lucide-react';
import chatService from '../services/chatService';
import { getImageUrl } from '../utils/imageUrl';
import { showError } from '../services/toastService';
import './ChatWindow.css';

const ChatWindow = ({ room, socket, onRoomUpdate }) => {
    const [messages, setMessages] = useState([]);
    const [inputText, setInputText] = useState('');
    const [loading, setLoading] = useState(true);
    const [sending, setSending] = useState(false);
    const [isTyping, setIsTyping] = useState(false);
    const messagesEndRef = useRef(null);
    const fileInputRef = useRef(null);
    const typingTimeoutRef = useRef(null);

    useEffect(() => {
        if (room) {
            console.log('Room changed:', room.id);
            setMessages([]); // Clear previous messages
            loadMessages();
            joinRoom();
        }
    }, [room?.id]); // Only re-run when room ID changes

    useEffect(() => {
        if (socket && room) {
            setupSocketListeners();
            return () => {
                cleanupSocketListeners();
            };
        }
    }, [socket, room?.id]);

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    const joinRoom = () => {
        if (socket && room) {
            const adminData = JSON.parse(localStorage.getItem('adminUser') || '{}');
            console.log('Admin joining room:', room.id);
            socket.emit('join_room', {
                roomId: room.id,
                userId: adminData.id
            });
        }
    };

    const loadMessages = async () => {
        try {
            setLoading(true);
            const response = await chatService.getRoomMessages(room.id);
            if (response.success) {
                console.log('Loaded messages:', response.data.messages.length);
                setMessages(response.data.messages);

                // Mark all messages as read
                if (socket) {
                    const adminData = JSON.parse(localStorage.getItem('adminUser') || '{}');
                    socket.emit('mark_room_read', {
                        roomId: room.id,
                        userId: adminData.id
                    });
                }

                // Notify parent to update unread count
                onRoomUpdate();
            }
        } catch (error) {
            console.error('Load messages error:', error);
        } finally {
            setLoading(false);
        }
    };

    const setupSocketListeners = () => {
        if (!socket) return;

        console.log('Setting up socket listeners for room:', room.id);

        // New message received
        socket.on('new_message', (message) => {
            console.log('New message received:', message.id, 'for room:', message.room_id);
            if (message.room_id === room.id) {
                setMessages(prev => {
                    // Check if message already exists
                    if (prev.find(m => m.id === message.id)) {
                        console.log('Message already exists, skipping');
                        return prev;
                    }
                    console.log('Adding new message to list');
                    return [...prev, message];
                });

                // Mark as read immediately
                const adminData = JSON.parse(localStorage.getItem('adminUser') || '{}');
                socket.emit('message_read', {
                    messageId: message.id,
                    roomId: room.id,
                    userId: adminData.id
                });

                // Notify parent to update
                onRoomUpdate();
            }
        });

        // Typing indicator
        socket.on('user_typing', ({ userId, isTyping: typing }) => {
            if (userId === room.buyer_id) {
                console.log('Buyer typing:', typing);
                setIsTyping(typing);
            }
        });

        // Message read update
        socket.on('message_read_update', ({ messageId, readAt }) => {
            console.log('Message read update:', messageId);
            setMessages(prev =>
                prev.map(msg =>
                    msg.id === messageId ? { ...msg, read_at: readAt } : msg
                )
            );
        });

        // Room joined confirmation
        socket.on('room_joined', (data) => {
            console.log('Room joined successfully');
        });

        // Error handling
        socket.on('error', (error) => {
            console.error('Socket error:', error);
            showError(error.message || 'Connection error');
        });
    };

    const cleanupSocketListeners = () => {
        if (!socket) return;
        console.log('Cleaning up socket listeners');
        socket.off('new_message');
        socket.off('user_typing');
        socket.off('message_read_update');
        socket.off('room_joined');
        socket.off('error');
    };

    const handleSendMessage = async () => {
        if (!inputText.trim() || !socket) return;

        const messageText = inputText.trim();
        setInputText('');
        setSending(true);

        try {
            const adminData = JSON.parse(localStorage.getItem('adminUser') || '{}');

            console.log('Sending message:', messageText);
            socket.emit('send_message', {
                roomId: room.id,
                senderId: adminData.id,
                senderType: 'admin',
                message: messageText,
                messageType: 'text'
            });

            socket.emit('typing_stop', {
                roomId: room.id,
                userId: adminData.id
            });

            onRoomUpdate();
        } catch (error) {
            console.error('Send message error:', error);
            setInputText(messageText);
            showError('Failed to send message');
        } finally {
            setSending(false);
        }
    };

    const handleTyping = (text) => {
        setInputText(text);

        if (!socket) return;

        const adminData = JSON.parse(localStorage.getItem('adminUser') || '{}');

        socket.emit('typing_start', {
            roomId: room.id,
            userId: adminData.id,
            userName: 'Admin'
        });

        if (typingTimeoutRef.current) {
            clearTimeout(typingTimeoutRef.current);
        }

        typingTimeoutRef.current = setTimeout(() => {
            socket.emit('typing_stop', {
                roomId: room.id,
                userId: adminData.id
            });
        }, 1000);
    };

    const handleFileUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        try {
            setSending(true);
            const uploadResponse = await chatService.uploadFile(file);

            if (uploadResponse.success && socket) {
                const adminData = JSON.parse(localStorage.getItem('adminUser') || '{}');
                const messageType = file.type.startsWith('image/') ? 'image' : 'document';

                socket.emit('send_message', {
                    roomId: room.id,
                    senderId: adminData.id,
                    senderType: 'admin',
                    message: messageType === 'image' ? 'Sent an image' : 'Sent a document',
                    messageType,
                    fileUrl: uploadResponse.data.fileUrl,
                    fileName: uploadResponse.data.fileName,
                    fileSize: uploadResponse.data.fileSize
                });

                onRoomUpdate();
            }
        } catch (error) {
            console.error('Upload error:', error);
            showError('Failed to upload file');
        } finally {
            setSending(false);
        }
    };

    // No ticket/status concept: keep chat simple

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    return (
        <div className="chat-window">
            {/* Header */}
            <div className="chat-header">
                <div className="chat-header-info">
                    <h3>{room.buyer?.business_name || 'Unknown Buyer'}</h3>
                    <span className="buyer-status">
                        <Circle size={10} fill={room.isOnline ? '#10b981' : '#6b7280'} color={room.isOnline ? '#10b981' : '#6b7280'} />
                        {room.isOnline ? ' Online' : ' Offline'}
                    </span>
                </div>

                <div className="chat-header-actions" />
            </div>

            <div className="chat-body">
                {/* Messages Area */}
                <div className="messages-area">
                    {loading ? (
                        <div className="loading-messages">Loading messages...</div>
                    ) : (
                        <>
                            <div className="messages-list">
                                {messages.map((message) => {
                                    const isAdmin = message.sender_type === 'admin';
                                    const isSystem = message.message_type === 'system';

                                    if (isSystem) {
                                        return (
                                            <div key={message.id} className="system-message">
                                                <span>{message.message}</span>
                                            </div>
                                        );
                                    }

                                    return (
                                        <div
                                            key={message.id}
                                            className={`message ${isAdmin ? 'admin-message' : 'buyer-message'}`}
                                        >
                                            <div className="message-bubble">
                                                {message.message_type === 'image' && message.file_url && (
                                                    <div className="message-image-container">
                                                        <ImageIcon size={16} className="file-icon" />
                                                        <img
                                                            src={getImageUrl(message.file_url)}
                                                            alt="Attachment"
                                                            className="message-image"
                                                            onClick={() => window.open(getImageUrl(message.file_url), '_blank')}
                                                        />
                                                    </div>
                                                )}

                                                {message.message_type === 'document' && message.file_url && (
                                                    <a
                                                        href={getImageUrl(message.file_url)}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="message-document"
                                                    >
                                                        <FileText size={16} className="file-icon" />
                                                        {message.file_name || 'Document'}
                                                        <Download size={14} className="download-icon" />
                                                    </a>
                                                )}

                                                {message.message && (
                                                    <p className="message-text">{message.message}</p>
                                                )}

                                                <div className="message-footer">
                                                    <span className="message-time">
                                                        {new Date(message.created_at || message.createdAt).toLocaleTimeString('en-US', {
                                                            hour: '2-digit',
                                                            minute: '2-digit'
                                                        })}
                                                    </span>
                                                    {isAdmin && (
                                                        <span className="message-status">
                                                            {message.read_at ? (
                                                                <CheckCheck size={14} color="#10b981" />
                                                            ) : message.delivered_at ? (
                                                                <CheckCheck size={14} color="#6b7280" />
                                                            ) : (
                                                                <Check size={14} color="#6b7280" />
                                                            )}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                                <div ref={messagesEndRef} />
                            </div>

                            {isTyping && (
                                <div className="typing-indicator">
                                    <span>{room.buyer?.business_name} is typing...</span>
                                </div>
                            )}

                            {/* Input Area */}
                            <div className="input-area">
                                <div className="input-container">
                                    <input
                                        type="file"
                                        ref={fileInputRef}
                                        style={{ display: 'none' }}
                                        onChange={handleFileUpload}
                                        accept="image/*,.pdf,.doc,.docx"
                                    />

                                    <textarea
                                        className="message-input"
                                        placeholder="Type a message..."
                                        value={inputText}
                                        onChange={(e) => handleTyping(e.target.value)}
                                        onKeyPress={(e) => {
                                            if (e.key === 'Enter' && !e.shiftKey) {
                                                e.preventDefault();
                                                handleSendMessage();
                                            }
                                        }}
                                        rows={1}
                                        disabled={sending}
                                    />

                                    <button
                                        className="send-button"
                                        onClick={handleSendMessage}
                                        disabled={!inputText.trim() || sending}
                                    >
                                        <Send size={20} />
                                    </button>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ChatWindow;
